import { Fragment } from "react";

import { CaretDownIcon } from "@phosphor-icons/react";
import {
  Box,
  Button,
  Flex,
  Popover,
  Separator,
  Tabs,
  Text,
} from "@radix-ui/themes";

import type { AccountOption } from "./accountSwitcher.utils";
import "./accountSwitcher.css";

export type AccountProduct = AccountOption["product"];

export interface AccountSwitcherPresentationProps {
  /** Currently selected account id, or undefined before one is chosen. */
  value: string | undefined;
  options: AccountOption[];
  triggerLabel?: string;
  isLoading?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeProduct: AccountProduct;
  onActiveProductChange: (product: AccountProduct) => void;
  hasLiveAccounts: boolean;
  hasPaperAccounts: boolean;
  onAccountSelect: (accountId: string) => void;
}

export const AccountSwitcherPresentation = ({
  value,
  options,
  triggerLabel,
  isLoading = false,
  open,
  onOpenChange,
  activeProduct,
  onActiveProductChange,
  hasLiveAccounts,
  hasPaperAccounts,
  onAccountSelect,
}: AccountSwitcherPresentationProps) => {
  const selectedOption = options.find((option) => option.id === value);

  if (isLoading || options.length === 0) {
    const label = isLoading ? "Loading accounts…" : "No accounts";
    return (
      <Button
        variant="soft"
        color="gray"
        highContrast
        aria-label={`Account: ${label}`}
        disabled
      >
        <Text>{label}</Text>
        <CaretDownIcon size={12} aria-hidden />
      </Button>
    );
  }

  const visibleTriggerLabel =
    triggerLabel ?? selectedOption?.label ?? "Select account";

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger>
        <Button
          variant="soft"
          color="gray"
          highContrast
          aria-label={`Account: ${visibleTriggerLabel}`}
        >
          <Text>{visibleTriggerLabel}</Text>
          <CaretDownIcon size={12} aria-hidden />
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        size="1"
        data-advanced-trading-dashboard
        className="atd-account-switcher-content w-fit"
      >
        <Tabs.Root
          value={activeProduct}
          onValueChange={(product) =>
            onActiveProductChange(product as AccountProduct)
          }
        >
          <Flex pb="1">
            <Tabs.List size="1" aria-label="Account type" className="px-1">
              <Tabs.Trigger
                value="live"
                aria-label="Live accounts"
                disabled={!hasLiveAccounts}
              >
                <Text>Live</Text>
              </Tabs.Trigger>
              <Tabs.Trigger
                value="paper"
                aria-label="Paper accounts"
                disabled={!hasPaperAccounts}
              >
                <Text>Paper</Text>
              </Tabs.Trigger>
            </Tabs.List>
          </Flex>
          {(["live", "paper"] as const).map((product) => (
            <Tabs.Content key={product} value={product}>
              <Box className="atd-account-switcher-list">
                <Flex direction="column">
                  {options
                    .filter((option) => option.product === product)
                    .map((option, index, productOptions) => (
                      <Fragment key={option.id}>
                        <Button
                          size="2"
                          variant="ghost"
                          color="gray"
                          highContrast
                          aria-pressed={option.id === value}
                          className="atd-account-switcher-item w-full justify-start"
                          onClick={() => onAccountSelect(option.id)}
                        >
                          <Flex
                            direction="column"
                            align="stretch"
                            gap="1"
                            className="w-full"
                          >
                            <Text
                              size="1"
                              weight="bold"
                              truncate
                              className="text-left"
                            >
                              {option.label}
                            </Text>
                            <Flex align="center" justify="between" gap="4">
                              <Text size="1" color="gray">
                                {option.accountNumber}
                              </Text>
                              {option.balance ? (
                                <Text
                                  size="1"
                                  color="gray"
                                  className="atd-numeric"
                                >
                                  {option.balance}
                                </Text>
                              ) : null}
                            </Flex>
                          </Flex>
                        </Button>
                        {index < productOptions.length - 1 ? (
                          <Separator size="4" my="1" />
                        ) : null}
                      </Fragment>
                    ))}
                </Flex>
              </Box>
            </Tabs.Content>
          ))}
        </Tabs.Root>
      </Popover.Content>
    </Popover.Root>
  );
};
